// MOCK DATA (frontend-only). Replace with backend later if needed.

export const NAV_LINKS = [
  { id: "services", label: "Services", href: "/#services" },
  { id: "how-we-work", label: "Process", href: "/#how-we-work" },
  { id: "mcp-section", label: "UTU MCP", href: "/#mcp" },
  { id: "case-studies", label: "Case Studies", href: "/#case-studies" },
  { id: "playground", label: "Playground", href: "/mcp/playground" },
  { id: "docs", label: "Docs", href: "/mcp/docs" },
  { id: "insights", label: "Insights", href: "/insights" },
  { id: "about", label: "About", href: "/about" },
];

// Service Verticals for Bento Grid - 4 cards
export const SERVICE_CARDS = [
  {
    id: "custom-ai-models",
    title: "Custom AI Models (Local/Private)",
    subtitle: "Sovereign AI Infrastructure",
    description: "Fine-tune and train open-weights models (Llama, Mistral, Qwen) that run fully locally on client hardware or within secure private VPNs. Ensure proprietary institutional data never leaves your organisation.",
    tags: ["SLMs", "Local Inference", "Data Sovereignty"],
    href: "/services/custom-ai-models",
  },
  {
    id: "utu-mcp-server",
    title: "UTU MCP Server",
    subtitle: "Linguistic Context Layer",
    description: "Production-ready Model Context Protocol (MCP) server cluster supporting 61+ African languages. Inject native cultural context, regional dialect nuances, and real-time morphological analysis into your LLM stack.",
    tags: ["MCP Protocol", "61+ Languages", "Low Latency"],
    href: "/mcp/docs",
  },
  {
    id: "custom-software",
    title: "Custom Software Systems",
    subtitle: "Full-Stack System Engineering",
    description: "You describe the requirements; we build the system. End-to-end engineering of modern web applications, high-throughput APIs, and distributed architectures optimized for low-bandwidth networks.",
    tags: ["Full-Stack", "Scalability", "API Rigor"],
    href: "/services/custom-software",
  },
  {
    id: "research-rnd",
    title: "Research & R&D",
    subtitle: "Linguistic NLP & Novel Architectures",
    description: "Exploratory research and development on low-resource language tokenization, code-mixing resolution (Sheng, Swahili, etc.), and lightweight architectures designed to bypass standard context limits.",
    tags: ["Agglutinative Grammar", "NLP Research", "Prototypes"],
    href: "/services/research-rnd",
  },
];

// How We Work - Engagement Models
export const ENGAGEMENT_TIERS = [
  {
    id: "audit",
    name: "Architecture Audit",
    duration: "2 Weeks",
    scope: "Fixed Price",
    priceText: "Fixed price advisory",
    description: "An intensive review of your current tech stack, databases, and AI pipelines. We identify system performance bottlenecks, security vulnerabilities, and provide a concrete architectural roadmap.",
    deliverables: [
      "Technical architecture roadmap document",
      "Security audit & vulnerability scanning reports",
      "Custom optimization proposals for databases & LLMs",
      "2-hour collaborative whiteboard review session",
    ],
    ctaText: "Book Architecture Audit",
    href: "/engage/tiers",
  },
  {
    id: "sprint",
    name: "Sprint Build",
    duration: "4-8 Weeks",
    scope: "Fixed Scope",
    priceText: "Milestone-based pricing",
    description: "Rapid, production-ready engineering sprints. We construct your custom software platforms, configure private AI infrastructure, or build production MCP integrations with absolute focus and velocity.",
    deliverables: [
      "Production-ready codebase (GitHub access)",
      "Automated CI/CD pipelines & Docker setup",
      "Documentation & code walkthrough session",
      "30-day post-delivery bug-fix warranty",
    ],
    ctaText: "Request a Quote",
    href: "/engage/tiers",
  },
  {
    id: "retainer",
    name: "Fractional Lab Retainer",
    duration: "Ongoing Monthly",
    scope: "Monthly Partnership",
    priceText: "Monthly subscription model",
    description: "Ongoing advisory, security audits, and dedicated scaling engineering. We act as your fractional R&D lab, providing continuous system improvements, model retraining, and scalability engineering.",
    deliverables: [
      "Dedicated senior engineers (fractional or full-time)",
      "Weekly progress updates & sprint planning",
      "Regular model monitoring & retraining",
      "Priority response SLA for critical system incidents",
    ],
    ctaText: "Establish Retainer Partnership",
    href: "/engage/tiers",
  },
];

// Expanded Case Studies with complete details
export const CASE_STUDIES = [
  {
    id: "masomogpt",
    headline: "MasomoGPT",
    subheadline: "Scaling Educational AI Across East Africa",
    hasMcpBadge: true,
    mcpBadgeText: "Powered by UTU MCP",
    challenge: "MasomoGPT, a multi-tenant AI tutoring platform, required robust, high-performance infrastructure capable of serving CBC curriculum-aligned educational content across 4 East African nations. The system had to support low-latency interactions on standard mobile connections, maintain strict data residency compliance, and safeguard user privacy for learners under 18.",
    solution: "We engineered an event-driven serverless architecture incorporating region-specific data residency clusters. Leveraging the UTU MCP Server, we built a localized context injection layer that translated curriculum queries, resolved regional linguistic nuances, and validated student interactions. An automated real-time moderation pipeline ensures safe content filtering before responses reach the client.",
    stackReasoning: "Built using FastAPI for low-overhead asynchronous execution, Next.js for high SEO and server-side rendering, and the UTU MCP protocol to query regional knowledge bases. PostgreSQL was chosen for absolute ACID compliance with customer tenancy, and Redis handled low-latency token caching.",
    results: [
      { metric: "4", label: "Countries Deployed" },
      { metric: "10k+", label: "Learners Reached" },
      { metric: "99.9%", label: "Uptime SLA" },
    ],
    lessons: "Agglutinative language processing (like Swahili prefixes) originally caused tokenization bloat. Integrating a custom Swahili tokenizer into our MCP layer prior to LLM submission reduced prompt token usage by 42% and drastically reduced API cost-per-query.",
    tags: ["AI Infrastructure", "Multi-Tenancy", "UTU MCP Integration"],
  },
  {
    id: "roundpesa",
    headline: "RoundPesa",
    subheadline: "Micro-payment Infrastructure and Currency Clearing",
    hasMcpBadge: false,
    challenge: "RoundPesa needed a cross-border micro-payment engine processing high-volume transactions across diverse East and West African mobile money APIs. The system had to handle unstable network timeouts, provide instant settlement verification, and execute automated currency conversion with minimal transaction slippage.",
    solution: "We constructed a microservices-based transaction orchestration engine utilizing Python and Django. We implemented a robust idempotency framework and transaction ledger with automatic retry states. A private vector index handles real-time liquidity routing across settlement corridors, matching liquidity providers dynamically.",
    stackReasoning: "Django was selected for its robust ORM and security ecosystem, guaranteeing transaction integrity. Celery and RabbitMQ managed asynchronous payment queues, and PostgreSQL stored financial logs with strict transactional constraints.",
    results: [
      { metric: "<3.2s", label: "Settlement Latency" },
      { metric: "Zero", label: "Double-Spend Incidents" },
    ],
    lessons: "Handling unstable mobile network gateway timeouts requires double-check endpoints. We had to implement a dual-callback architecture that verified state on both client push and provider pull to avoid duplicate disbursements under heavy load.",
    tags: ["Fintech", "Payment Ledgers", "Asynchronous Sprints"],
  },
  {
    id: "proam-realestate",
    headline: "ProamRealestate",
    subheadline: "Real Estate Platform Modernization",
    hasMcpBadge: false,
    challenge: "A legacy property discovery and listing engine was crippled by slow search response times (often exceeding 5 seconds), outdated visuals, and failure to process high-resolution media uploads from agents under poor mobile network conditions.",
    solution: "We rebuilt the platform's frontend using React with specialized image resizing pipelines (next-gen WebP generation). The backend search was migrated to a fast-indexing search cluster, enabling real-time geospatial search and property alerts. We added a lightweight agent dashboard for offline-first draft listing saving.",
    stackReasoning: "React was paired with Tailwind CSS to ensure a sleek, responsive, and component-driven architecture. FastAPI handles media ingestion and talks directly to a search index, bypassing heavier frameworks for raw listing query performance.",
    results: [
      { metric: "60%", label: "Faster Load Times" },
      { metric: "3x", label: "Traffic Capacity" },
      { metric: "94", label: "Lighthouse Score" },
    ],
    lessons: "Storing agent drafts locally on-device using IndexDB before pushing to the cloud resolved agent complaints regarding listing loss due to dropped cellular signals in remote areas.",
    tags: ["Real Estate", "Image Pipelines", "Geospatial Search"],
  },
  {
    id: "utu-infrastructure",
    headline: "UTU Language Infrastructure",
    subheadline: "Production-Ready Sovereign AI Context Layer",
    hasMcpBadge: true,
    mcpBadgeText: "Flagship Product",
    challenge: "No existing commercial LLM or open weights model processed native African languages at production scale. Existing systems suffered from high hallucination rates, lack of local idiom comprehension, and high token costs due to improper vocabulary tokenization.",
    solution: "We built and deployed the UTU MCP Server, serving as a dedicated cultural and linguistic memory layer. We trained custom tokenizers for agglutinative structures, Sheng code-mixing, and Swahili dialects. The server intercepts requests, optimizes vocabularies, and injects precise, versioned local knowledge nodes into the context window.",
    stackReasoning: "Built using Rust for absolute memory safety and raw CPU-level tokenization speed, and Python for the dataset curation tools. The service interfaces directly with OpenAI, Anthropic, and Llama instances over secure WebSocket and SSE endpoints.",
    results: [
      { metric: "61+", label: "Languages Supported" },
      { metric: "50K+", label: "Daily Requests" },
      { metric: "<50ms", label: "Tokenizing Latency" },
    ],
    lessons: "Injecting context blindly can bloat prompt size. We implemented a vector-similarity ranking algorithm within the MCP layer that limits context injection to only the top 3 most relevant knowledge nodes, reducing average context overhead by 70%.",
    tags: ["Linguistic NLP", "Sovereign AI", "Rust & Python"],
  },
];

// Technology Stack with reasoning
export const TECH_STACK = [
  { name: "FastAPI", category: "Backend", reason: "Asynchronous, lightweight routing for high-concurrency API integrations." },
  { name: "Django", category: "Backend", reason: "Secure, battle-tested ORM and administration console for transaction-critical software." },
  { name: "React", category: "Frontend", reason: "Dynamic state management and rich user interfaces for client dashboards." },
  { name: "Next.js", category: "Frontend", reason: "High SEO capabilities, static site generation, and server-side rendering." },
  { name: "MCP Protocol", category: "AI Layer", reason: "Open standard for connecting client-side data and local context to AI models." },
  { name: "Vector Databases", category: "Data Layer", reason: "Efficient, low-latency semantic search and retrieval-augmented generation (RAG)." },
  { name: "PostgreSQL", category: "Database", reason: "Reliable, ACID-compliant database ensuring transactional consistency for billing systems." },
  { name: "Docker", category: "DevOps", reason: "Containerization ensuring clean development-to-production parity for client delivery." },
  { name: "Kubernetes", category: "DevOps", reason: "Self-healing orchestration layer for scaling high-traffic API clusters." },
];

export const INSIGHTS_ARTICLES = [
  {
    id: "mcp-architecture",
    title: "Inside the UTU MCP Server Architecture",
    summary: "A technical breakdown of how we deploy Model Context Protocol servers to inject localized linguistic context with sub-50ms latency.",
    date: "June 28, 2026",
    readTime: "6 min read",
    author: "Aaron K. (Lead Architect)",
    content: "Deploying generative AI in regional African markets presents two massive roadblocks: prohibitive token costs and lack of local context. Our flagship Model Context Protocol (MCP) server cluster solves both. By parsing input prompts, mapping regional linguistic dependencies, and performing semantic search on local vector index bases, we construct an inspectable, audited context envelope. This is then injected dynamically into model calls. In this article, we map the entire pipeline from token optimization to local hosting.",
  },
  {
    id: "low-resource-tokenization",
    title: "Fixing Tokenization for Agglutinative African Languages",
    summary: "Why standard GPT/Claude tokenizers fail Swahili and Sheng speakers, and how custom tokenizers cut token counts by over 40%.",
    date: "May 14, 2026",
    readTime: "8 min read",
    author: "Dr. L. Maina (NLP Researcher)",
    content: "Standard tokenizers break words into sub-word tokens based on Western vocabularies. For agglutinative languages like Swahili, where nouns, tenses, and subjects are merged as prefixes and suffixes, this approach is disastrous. Words like 'tulivyovisoma' (which translates to 'as we read them') are split into 6 or 7 tokens, inflating costs and degrading semantic coherence. By retraining Byte-Pair Encoding (BPE) tokenizers on local Swahili corpora, we've stabilized token boundaries, resulting in shorter sequences and cleaner comprehension.",
  },
  {
    id: "private-slm-scaling",
    title: "Scaling Small Language Models on Private Hardware",
    summary: "How we train and deploy 7B parameters SLMs to run locally on client servers, ensuring strict data sovereignty.",
    date: "April 02, 2026",
    readTime: "10 min read",
    author: "Aaron K. (Lead Architect)",
    content: "For enterprise customers, transmitting sensitive records to public third-party APIs is a non-starter. Our private SLM deployment model fine-tunes open-weights models (like Llama-3-8B and Qwen-2.5-7B) on private VPN clusters. We outline the hardware configurations, quantization strategies (like 4-bit AWQ), and model guardrails that enable custom inference to run securely on-premise without losing performance.",
  },
];

// Contact/Booking CTA
export const BOOKING_URL = "https://cal.com/utu-engineering/architecture-audit";
export const CONSULTATION_URL = "https://cal.com/utu-engineering/consultation";
export const CONTACT_EMAIL = "aaron@utuafrica.com";

export const FAQS = [
  {
    id: "model-training",
    q: "Do you train models from scratch or fine-tune existing ones?",
    a: "Both. We train specialized low-resource language models and fine-tune open-weights models (Llama, Qwen, Mistral) on private, curated datasets to handle specific regional idioms, agglutinative morphology, and code-mixing structures (like Swahili-English-Sheng).",
  },
  {
    id: "mcp-servers",
    q: "What are MCP servers and why are they necessary?",
    a: "Model Context Protocol (MCP) servers act as a standardized bridging layer between AI models and external data sources. Our server cluster provides a cultural and linguistic context index, enabling standard LLMs to understand local idioms and regulatory conditions without hallucination.",
  },
  {
    id: "data-sovereignty",
    q: "How do you guarantee data sovereignty?",
    a: "We set up custom models and databases that execute fully on your private clouds, hardware, or VPN. Your corporate intelligence and client records never leave your network boundary, ensuring absolute compliance with local data protection regulations.",
  },
  {
    id: "tokenization",
    q: "How does tokenization affect our AI costs?",
    a: "Standard LLM APIs bill by token usage. Standard tokenizers treat African languages inefficiently, inflating the token count by 3-4x compared to English text. Our custom tokenizers break words into meaningful morphemes, reducing token size by 40-50% and cutting inference costs proportionally.",
  },
];

export const PROCESS_TAGS = [
  "Swahili",
  "Sheng",
  "Kikuyu",
  "Luo",
  "Luganda",
  "Zulu",
  "Tokenization",
  "Fine-tuning",
  "MCP Protocol",
  "RAG Systems",
];

export const DEMO_PRESETS = [
  {
    id: "sheng",
    label: "Sheng (Slang)",
    value: "Form ni gani leo wadau?",
    translation: "What's the plan today, guys?",
    context: "Sheng slang, highly context-dependent, combining Swahili grammar with English/vernacular vocabulary.",
    tokens: ["Form", " ni", " gani", " leo", " wad", "au"],
  },
  {
    id: "swahili",
    label: "Kiswahili (Agglutinative)",
    value: "Nitatumiwa vitabu hivyo kesho.",
    translation: "I will be sent those books tomorrow.",
    context: "Agglutinative syntax with complex prefix chains (Ni-ta-tum-i-w-a) for subject, tense, and passive aspects.",
    tokens: ["Ni", "ta", "tumi", "wa", " vitabu", " hivyo", " kesho"],
  },
  {
    id: "code-mixing",
    label: "Code-Mixing",
    value: "Niko kwa meeting, nitacall back later.",
    translation: "I am in a meeting, I will call you back later.",
    context: "Common urban conversational pattern mixing Swahili helping verbs ('niko kwa', 'nita') with English nouns and verbs.",
    tokens: ["Niko", " kwa", " meeting", ",", " nita", "call", " back", " later"],
  },
  {
    id: "kikuyu",
    label: "Gikuyu (Low-resource)",
    value: "Wimwega muno, ni wega.",
    translation: "You are very good, thank you.",
    context: "Bantu language spoken in Central Kenya, characterized by specific phonetic scales and tone markers.",
    tokens: ["Wi", "mwega", " muno", ",", " ni", " wega"],
  },
];

export const FOOTER_LINKS = [
  { id: "home", label: "Home", href: "/" },
  { id: "about", label: "About Lab", href: "/about" },
  { id: "docs", label: "Developer Docs", href: "/mcp/docs" },
  { id: "playground", label: "Playground", href: "/mcp/playground" },
  { id: "insights", label: "Insights", href: "/insights" },
];
